import {
    FilterAirline,
    FilterData,
    FilterDurationRange,
    FilterLayoverAirport,
    FilterNbAirport,
    FilterPriceRange,
    FilterStops,
    FilterTimeOptions,
    FlightCard,
    Sectors
} from '@/src/types/flight'
import { MulticitySector } from '@/src/types/searchQuery'

const TIME_SLOT_OPTIONS = ['0600', '1200', '1800', '2000']

type JourneyStats = {
    stopsMinPrice: Map<number, number>
    tripDurationMin: number
    tripDurationMax: number
    layoverDurationMin: number
    layoverDurationMax: number
    layoverAirports: Set<string>
}

const createEmptyJourneyStats = (): JourneyStats => ({
    stopsMinPrice: new Map(),
    tripDurationMin: Infinity,
    tripDurationMax: -Infinity,
    layoverDurationMin: Infinity,
    layoverDurationMax: -Infinity,
    layoverAirports: new Set()
})

const updateMin = (current: number, value: number) => Math.min(current, value)
const updateMax = (current: number, value: number) => Math.max(current, value)

const minutesToHours = (minutes: number) => (minutes > 0 ? Math.ceil(minutes / 60) : 0)

const buildDurationRange = (minMinutes: number, maxMinutes: number): FilterDurationRange => {
    const min = minutesToHours(minMinutes === Infinity ? 0 : minMinutes)
    const max = minutesToHours(maxMinutes === -Infinity ? 0 : maxMinutes)
    return { min, max, current: { min, max } }
}

const buildEmptyDurationRange = (): FilterDurationRange => ({
    min: 0,
    max: 0,
    current: { min: 0, max: 0 }
})

const buildStopsFilter = (stats: JourneyStats): FilterStops => {
    const options = Array.from(stats.stopsMinPrice.keys()).sort((a, b) => a - b)
    return {
        options,
        prices: options.map(stop => stats.stopsMinPrice.get(stop) ?? 0),
        selectedValues: []
    }
}

const buildAirlineFilter = (airlinesMinPrice: Map<string, number>, airlineNames: Record<string, string>): FilterAirline => ({
    options: Array.from(airlinesMinPrice.entries())
        .map(([value, priceLabel]) => ({
            label: airlineNames[value] || value,
            value,
            priceLabel
        }))
        .sort((a, b) => a.label.localeCompare(b.label)),
    selectedValues: [],
    showNoResultsWhenNoneSelected: false,
    showMultiAirlineItineraries: false
})

const buildTimeFilter = (airportCode: string): FilterTimeOptions => ({
    airportCode,
    options: [...TIME_SLOT_OPTIONS],
    selectedValues: []
})

const buildLayoverAirportFilter = (stats: JourneyStats): FilterLayoverAirport => ({
    values: Array.from(stats.layoverAirports).sort(),
    selectedOptions: []
})

const getSectorAirlineCodes = (sectors: Sectors, sectorKey?: string): string[] => {
    const segments = sectors[sectorKey || '']?.flights?.segments ?? []
    return Array.from(new Set(segments.map(segment => segment?.ac).filter((code): code is string => !!code)))
}

const collectLayoverAirports = (sectors: Sectors, sectorKey?: string, target?: Set<string>) => {
    const segments = sectors[sectorKey || '']?.flights?.segments ?? []
    if (segments.length <= 1) return
    segments.slice(0, -1).forEach(segment => {
        const code = segment?.arrival?.airportCode
        if (code) target?.add(code)
    })
}

const getCardPrice = (card: FlightCard) => card.priceBreakup?.pr ?? 0

/**
 * Builds client-side filterData for multicity when the API returns empty filter objects.
 * filterData[filterJourneyIndex] maps to journey 1..N (same indexing as cards[0] sectorKeys).
 * activeRtLeg stays 0 for card list; only filter UI passes filterJourneyIndex 0..N-1.
 * Price filter lives at journeyPrice[0] only (combined card price).
 */
export function buildMulticityFilterData ({
    cards,
    sectors,
    searchSectors,
    airlineNames = {}
}: {
    cards: FlightCard[]
    sectors: Sectors
    searchSectors: MulticitySector[]
    airlineNames?: Record<string, string>
}): { filterData: FilterData; journeyCount: number } {
    const journeyCount = searchSectors.length
    const journeyStats = Array.from({ length: journeyCount }, createEmptyJourneyStats)

    let priceMin = Infinity
    let priceMax = -Infinity
    const globalAirlinesMinPrice = new Map<string, number>()
    const fromNearbyByJourney: Set<string>[] = Array.from({ length: journeyCount }, () => new Set())
    const toNearbyByJourney: Set<string>[] = Array.from({ length: journeyCount }, () => new Set())

    for (const card of cards) {
        const cardPrice = getCardPrice(card)
        priceMin = updateMin(priceMin, cardPrice)
        priceMax = updateMax(priceMax, cardPrice)

        for (let journeyIndex = 0; journeyIndex < journeyCount; journeyIndex++) {
            const sectorKey = card.sectorKeys?.[journeyIndex]
            const sector = sectorKey ? sectors[sectorKey] : undefined
            if (!sector) continue

            const stats = journeyStats[journeyIndex]
            const stops = sector.stops ?? 0
            const existingStopPrice = stats.stopsMinPrice.get(stops)
            if (existingStopPrice === undefined || cardPrice < existingStopPrice) {
                stats.stopsMinPrice.set(stops, cardPrice)
            }

            getSectorAirlineCodes(sectors, sectorKey).forEach(airlineCode => {
                const existingGlobalPrice = globalAirlinesMinPrice.get(airlineCode)
                if (existingGlobalPrice === undefined || cardPrice < existingGlobalPrice) {
                    globalAirlinesMinPrice.set(airlineCode, cardPrice)
                }
            })

            const tripDurationMinutes =
                card.tripDurationMapBySector?.[journeyIndex] ?? sector.totalSectorDuration ?? 0
            stats.tripDurationMin = updateMin(stats.tripDurationMin, tripDurationMinutes)
            stats.tripDurationMax = updateMax(stats.tripDurationMax, tripDurationMinutes)

            const layoverDurationMinutes = card.layoverDurationMapBySector?.[journeyIndex] ?? 0
            stats.layoverDurationMin = updateMin(stats.layoverDurationMin, layoverDurationMinutes)
            stats.layoverDurationMax = updateMax(stats.layoverDurationMax, layoverDurationMinutes)

            collectLayoverAirports(sectors, sectorKey, stats.layoverAirports)

            if (sector.nbAirports?.length) {
                const segments = sector.flights?.segments ?? []
                const firstDep = segments[0]?.departure?.airportCode?.toUpperCase()
                const lastArr = segments[segments.length - 1]?.arrival?.airportCode?.toUpperCase()
                for (const nb of sector.nbAirports) {
                    const code = nb.toUpperCase()
                    if (code === firstDep) fromNearbyByJourney[journeyIndex].add(code)
                    if (code === lastArr) toNearbyByJourney[journeyIndex].add(code)
                }
            }
        }
    }

    const priceRange: FilterPriceRange = {
        min: priceMin === Infinity ? 0 : priceMin,
        max: priceMax === -Infinity ? 0 : priceMax,
        current: {
            min: priceMin === Infinity ? 0 : priceMin,
            max: priceMax === -Infinity ? 0 : priceMax
        }
    }

    const filterData: FilterData = {
        journeys: searchSectors.map(sector => ({ from: sector.from, to: sector.to })),
        NewStops: journeyStats.map(buildStopsFilter),
        departureTimeFrom: searchSectors.map(sector => buildTimeFilter(sector.from)),
        arrivalTimeFrom: searchSectors.map(sector => buildTimeFilter(sector.to)),
        airlineFilter: [buildAirlineFilter(globalAirlinesMinPrice, airlineNames)],
        tripDuration: journeyStats.map(stats => buildDurationRange(stats.tripDurationMin, stats.tripDurationMax)),
        layoverDuration: journeyStats.map(stats =>
            buildDurationRange(stats.layoverDurationMin, stats.layoverDurationMax)
        ),
        layover_Airport: journeyStats.map(buildLayoverAirportFilter),
        nbAirportFilter: searchSectors.map((sector, journeyIndex) => [
            {
                airportCode: sector.from,
                options: Array.from(fromNearbyByJourney[journeyIndex]).map(code => ({ label: code, value: code, subLabel: '' })),
                selectedValues: []
            },
            {
                airportCode: sector.to,
                options: Array.from(toNearbyByJourney[journeyIndex]).map(code => ({ label: code, value: code, subLabel: '' })),
                selectedValues: []
            }
        ]),
        journeyPrice: [priceRange]
    }

    if (!cards.length) {
        filterData.NewStops = searchSectors.map(() => ({ options: [], prices: [], selectedValues: [] }))
        filterData.airlineFilter = [{ options: [], selectedValues: [], showNoResultsWhenNoneSelected: false, showMultiAirlineItineraries: false }]
        filterData.tripDuration = searchSectors.map(buildEmptyDurationRange)
        filterData.layoverDuration = searchSectors.map(buildEmptyDurationRange)
        filterData.layover_Airport = searchSectors.map(() => ({ values: [], selectedOptions: [] }))
    }


    console.log('filterData', filterData)
    return { filterData, journeyCount }
}
